import { useState, useEffect } from "react";
import { Modal, Form, InputGroup } from "react-bootstrap";
import { Upload, Plus, X } from "lucide-react";
import { FaSearch } from "react-icons/fa";
import ResourceCard from "./ResourceCard";
import localization from "services/localization";
import { getResourceType, getResourceIcon } from "services/utils";
import ResourcePreview from "./ResourcePreview";
import { ResourceTable } from "./ResourceTable";
import DeleteModal from "../DeleteModal";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "context/useUserStore";
import { toast } from "react-toastify";
import { TooltipProvider } from "components/ui/tooltip_v2";

export default function ResourceLibrary({
  resources = [],
  onSelectResources,
  onUploadClick,
  showAdd = true,
  setResources,
  loading,
  setLoading,
  selectedIds,
  onRemoveSelected,
}) {
  const [showViewModal, setShowViewModal] = useState(false);
  const [itemToView, setItemToView] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemName, setItemName] = useState("");
  const { appUser, userData } = useUserStore();

  const handleView = (item) => {
    setItemToView(item);
    setShowViewModal(true);
  };

  useEffect(() => {
    filterResources();
  }, [searchTerm, resources]);

  const filterResources = () => {
    let filtered = resources;
    if (searchTerm) {
      filtered = filtered.filter((res) =>
        res.originalName.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredItems(filtered);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(item.originalName || "");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deleteResources(
        itemToDelete.id,
        userData?.selected_school_id,
      );
      // await SpecificAPI.updateResources(
      //   userData?.selected_school_id,
      //   itemToDelete.id,
      //   {
      //     enabled: false,
      //   },
      // );

      setResources((prev) => prev.filter((ev) => ev.id !== itemToDelete.id));

      toast.success("Resource deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete resource. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  return (
    <div className="resource-library">
      <div className="flex justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">
            {localization.resource_library || "Resource Library"}
          </h1>
          <p className="description_text">
            {localization.browse_manage_resources ||
              "Browse and manage your resources"}
          </p>
        </div>
        {showAdd && (
          <button
            onClick={onUploadClick}
            className="action-btn primary flex items-center gap-2 px-4 py-2 rounded-lg"
          >
            <Plus size={18} />
            {localization.add_resource || "Add Resource"}
          </button>
        )}
      </div>
      <TooltipProvider delayDuration={100}>
        <ResourceTable
          resources={resources}
          onView={handleView}
          onDelete={handleDelete}
          selectable
          onSelectionChange={onSelectResources}
          selectedIds={selectedIds}
          onRemoveSelected={onRemoveSelected}
          userId={appUser?.id}
          isRoot={appUser?.role === "root"}
        />
      </TooltipProvider>

      {/* Filters Row */}
      {/* <div className="d-flex align-items-center gap-3 w-100 mt-4 mb-4">
        <InputGroup
          className="flex-grow-1"
          style={{
            backgroundColor: "var(--module-card-bg)",
            borderRadius: "0.5rem",
            overflow: "hidden",
            border: `1px solid var(--module-card-border-color)`,
          }}
        >
          <InputGroup.Text id="search-addon" className="customSearchAddon">
            <FaSearch className="text-primary" />{" "}
          </InputGroup.Text>
          <Form.Control
            placeholder={
              localization.search_module_placeholder ||
              "Search by module name..."
            }
            aria-label="Search"
            aria-describedby="search-addon"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="customSearchInput"
            style={{}}
          />
        </InputGroup>
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div
          className={
            selectedResources.length > 0 ? "col-span-8" : "col-span-12"
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.length > 0 &&
              filteredItems.map((resource) => {
                const type = getResourceType(resource);
                const icon = getResourceIcon(type);
                const url = resource.signed_url || resource.gcs?.public_url;

                return (
                  <ResourceCard
                    key={resource.id}
                    resource={{
                      ...resource,
                      type,
                      icon,
                      url,
                      uploadedAt: new Date(
                        resource.created_at,
                      ).toLocaleDateString(),
                      name: resource.originalName,
                    }}
                    isSelected={selectedResources.some(
                      (r) => r.id === resource.id,
                    )}
                    onSelect={() => onSelectResources(resource)}
                    hasView={true}
                    hasDelete={false}
                    onView={handleView}
                  />
                );
              })}
          </div>

          {resources.length === 0 && (
            <div className="text-center py-16">
              <Upload size={40} className="mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600">
                {localization.no_resources_yet || "No resources yet"}
              </p>
            </div>
          )}
        </div>

        {selectedResources.length > 0 && (
          <div className="col-span-4 resource-sidebar rounded-xl shadow p-4 flex flex-col">
            <h2 className="font-bold text-lg mb-4">
              {localization.selected || "Selected"} ({selectedResources.length})
            </h2>

            <div className="flex-1 space-y-3 overflow-y-auto">
              {selectedResources.map((res) => (
                <div key={res.id} className="resource-card">
                  <div className="min-w-0">
                    <p className="resource-name">{res.originalName}</p>
                    <p className="text-xs text-slate-500">
                      {getResourceType(res)}
                    </p>
                  </div>

                  <button
                    onClick={() => onRemoveSelected(res.id)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div> */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
        size="xl"
        backdrop="static"
        className="themed-modal addEditModal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {itemToView?.originalName || "View Resource"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "90vh", overflowY: "auto" }}>
          {itemToView && (
            <div className="w-full h-full flex items-center justify-center">
              {itemToView.type === "image" ? (
                <img
                  src={itemToView.url || "/placeholder.svg"}
                  alt={itemToView.originalName}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ResourcePreview resource={itemToView} />
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
}
